import React, { useRef } from 'react';
import type { ChipTone } from '../shared/listPage';

/**
 * Κοινά κομμάτια των σελίδων της Αποθήκης στη νέα εμφάνιση (14/10/2026): καρτέλες ενοτήτων, ημερομηνίες
 * ώρας Ελλάδας και η κατάσταση αποθέματος μιας πρώτης ύλης. Μόνο εμφάνιση — κανένα αίτημα εδώ.
 */

// Ημερομηνίες ελληνικές, ώρα Ελλάδας και 24ωρη (ο server είναι UTC, το κατάστημα όχι).
const ATHENS = 'Europe/Athens';

export const fmtDate = (value?: string | null): string => {
  if (!value) return '—';
  // Ημερολογιακή ημερομηνία (π.χ. purchase_date, στήλη DATE): αυτούσια, χωρίς να περάσει από ζώνη ώρας.
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('el-GR', { timeZone: ATHENS, day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const fmtDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const time = d.toLocaleTimeString('el-GR', { timeZone: ATHENS, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return `${fmtDate(value)} · ${time}`;
};

/** Ποσότητα με έως 3 δεκαδικά χωρίς μηδενικά στο τέλος (όπως η απογραφή και οι ελλείψεις: «2.5», όχι «2.500»). */
export const fmtQty = (value: number | string | null | undefined): string => {
  const n = parseFloat(String(value ?? 0));
  if (!Number.isFinite(n)) return '0';
  return n.toFixed(3).replace(/\.?0+$/, '') || '0';
};

export type StockState = 'out' | 'low' | 'ok' | 'over';

/**
 * Κατάσταση μιας πρώτης ύλης, με τους κανόνες της παλιάς σελίδας: απόθεμα ≤ 0 είναι πάντα «Εξαντλημένο»
 * (και όταν ο server γράφει άλλο), αλλιώς ό,τι λέει ο server (stock_status), αλλιώς «Επαρκές».
 */
export const stockStateOf = (stock: number, status?: string | null): StockState => {
  if (Number(stock) <= 0) return 'out';
  switch (status) {
    case 'LOW': return 'low';
    case 'OUT_OF_STOCK': return 'out';
    case 'OVERSTOCKED': return 'over';
    default: return 'ok';
  }
};

export const STOCK_TONES: Record<StockState, ChipTone> = { out: 'danger', low: 'warn', ok: 'ok', over: 'muted' };

export interface SectionTab<K extends string> {
  id: K;
  label: string;
}

/**
 * Καρτέλες ενοτήτων κάτω από την κεφαλίδα της σελίδας (ARIA tabs: βελάκια, Home/End). Ως 14/10 η Αποθήκη
 * είχε κάρτες με εικονίδιο για κάθε ενότητα και «Πίσω» για επιστροφή· τώρα όλες οι ενότητες φαίνονται εδώ.
 * Η γραμμή κάτω από τις καρτέλες είναι η πάνω γραμμή της γραμμής εργαλείων που ακολουθεί.
 */
export function SectionTabs<K extends string>({ tabs, active, onChange, label, idPrefix }: {
  tabs: Array<SectionTab<K>>;
  active: K;
  onChange: (id: K) => void;
  label: string;
  idPrefix: string;
}): React.ReactElement {
  const refs = useRef<Partial<Record<K, HTMLButtonElement | null>>>({});
  const onKey = (event: React.KeyboardEvent, index: number) => {
    let next = -1;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    onChange(tabs[next].id);
    refs.current[tabs[next].id]?.focus();
  };
  return (
    // Αναδίπλωση αντί για οριζόντια κύλιση: στο κινητό οι πέντε καρτέλες δεν χωρούν, και μια κρυμμένη δεξιά
    // καρτέλα («Αγορές») δεν τη μαντεύει κανείς.
    <div role="tablist" aria-label={label} className="flex flex-wrap gap-x-1 -mb-px" data-testid="warehouse-tabs">
      {tabs.map((tab, i) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            ref={el => { refs.current[tab.id] = el; }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`${idPrefix}-tabpanel`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={e => onKey(e, i)}
            className={`relative shrink-0 h-11 px-3 rounded-t-lg text-sm whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${selected
              ? 'text-brand font-semibold after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full after:bg-brand'
              : 'text-t3 font-medium hover:text-t1'}`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/** Μικρή επικεφαλίδα μέσα σε ενότητα ή συρτάρι (χωρίς εικονίδιο, χωρίς κουτί). */
export const BlockTitle: React.FC<{ children: React.ReactNode; className?: string; id?: string }> = ({ children, className = '', id }) => (
  <h3 id={id} className={`text-sm font-semibold text-t1 ${className}`.trim()}>{children}</h3>
);
